import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useAtomValue } from "jotai";
import { Trans, useTranslation } from "react-i18next";
import {
  MODEL_DIAGNOSIS_LAYERS,
  MODEL_DIAGNOSIS_USES,
  parseModelAvailabilityDiagnosis,
  type ModelAvailabilityDiagnosis,
  type ModelDiagnosisConclusion,
  type ModelDiagnosisFinding,
  type ModelDiagnosisParamValue,
  type ModelDiagnosisUse,
  type ModelDiagnosisVerdict,
} from "@dosia/shared";
import { apiCall } from "../lib/api-client.js";
import { ApiError, type Organization } from "../lib/api-types.js";
import { authAtom } from "../lib/auth-store.js";
import { useApiQuery } from "../lib/useApiQuery.js";
import { platformModelDiagnosisPath } from "../lib/api-paths.js";
import { cn } from "../lib/cn.js";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { FormField, PrimaryButton, Select, TextInput } from "../components/FormField.js";

/**
 * 「平台模型治理 → 诊断」页签（N-157）：超管问「员工 X 为什么用不了模型 Y」，按 N-156 只读接口
 * `GET /api/platform/model-diagnosis` 的结果一屏看到每种用法卡在哪一层、证据和该谁处理。
 *
 * 只渲染、不判：总结论一律读服务端的 `conclusions`（判据单源在 core `diagnoseModelAvailability`），
 * 页面不从 `findings` 里自己挑「第一条拦下的」——冷却中的模型平台层先显「暂不可用」，但该谁处理是网关，
 * 这个归属只有 `conclusions[].owner` 里有。文案全部按原因码本地化（7 份 locale）。
 */

const VERDICT_CLASS: Record<ModelDiagnosisVerdict, string> = {
  pass: "border-dosia-success/50 bg-dosia-success/10 text-dosia-success",
  warn: "border-dosia-warning/50 bg-dosia-warning/10 text-dosia-warning",
  unavailable: "border-dosia-warning/50 bg-dosia-warning/10 text-dosia-warning",
  blocked: "border-dosia-text-secondary/50 bg-dosia-text-secondary/10 text-dosia-text",
  unknown: "border-dosia-border bg-dosia-bg-lighter text-dosia-text-secondary",
  not_applicable: "border-dosia-border bg-dosia-bg-lighter text-dosia-text-secondary",
};

const KNOWN_REASONS = new Set(["rate_limited", "no_credential", "quota_exhausted", "maintenance", "probe_failed", "adapter_pending"]);

const listOf = (v: ModelDiagnosisParamValue | undefined): string[] =>
  Array.isArray(v) ? v : typeof v === "string" && v ? [v] : [];

function VerdictBadge({ verdict }: { verdict: ModelDiagnosisVerdict }): JSX.Element {
  const { t } = useTranslation();
  return (
    <span className={cn("inline-block whitespace-nowrap rounded-full border px-2 py-px text-[11px]", VERDICT_CLASS[verdict])}>
      {t(`admin.modelDiagnosis.verdict.${verdict}`)}
    </span>
  );
}

function Code({ children }: { children?: ReactNode }): JSX.Element {
  return <code className="rounded bg-dosia-bg-lighter px-1 font-mono text-xs">{children}</code>;
}

/** 按原因码拼本地化句子；`params.hypothetical` 加「（假如下发了）」前缀。 */
function useFindingText(): { sentence: (f: ModelDiagnosisFinding) => string; evidence: (f: ModelDiagnosisFinding) => string; join: (items: readonly string[]) => string } {
  const { t } = useTranslation();
  const join = (items: readonly string[]): string => items.join(t("admin.modelDiagnosis.listSeparator"));
  const reasonLabel = (reason: ModelDiagnosisParamValue | undefined): string => {
    if (typeof reason !== "string" || !reason) return t("admin.modelDiagnosis.reason.none");
    return KNOWN_REASONS.has(reason) ? t(`admin.modelDiagnosis.reason.${reason}`) : reason;
  };
  const sentence = (f: ModelDiagnosisFinding): string => {
    const p = f.params ?? {};
    const prefix = p.hypothetical === true ? t("admin.modelDiagnosis.hypothetical") : "";
    return prefix + t(`admin.modelDiagnosis.code.${f.code}`, {
      scope: t(f.scope === "role" ? "admin.modelDiagnosis.scope.role" : "admin.modelDiagnosis.scope.org"),
      line: t(p.line === "tool" ? "admin.modelDiagnosis.line.tool" : "admin.modelDiagnosis.line.brain"),
      reason: reasonLabel(p.reason),
      groups: join(listOf(p.groups)),
      message: typeof p.message === "string" && p.message ? p.message : t("admin.modelDiagnosis.unknownCause"),
      status: typeof p.httpStatus === "number" ? t("admin.modelDiagnosis.httpStatus", { status: p.httpStatus }) : "",
    });
  };
  const evidence = (f: ModelDiagnosisFinding): string => {
    const p = f.params ?? {};
    const out: string[] = [];
    const groups = listOf(p.groups);
    if (groups.length) out.push(t("admin.modelDiagnosis.evidence.groups", { groups: join(groups) }));
    const protocols = listOf(p.protocols);
    if (protocols.length) out.push(t("admin.modelDiagnosis.evidence.protocols", { protocols: join(protocols) }));
    if (typeof p.variant === "string" && p.variant) out.push(t("admin.modelDiagnosis.evidence.variant", { variant: p.variant }));
    return out.join(" · ");
  };
  return { sentence, evidence, join };
}

function ConclusionHeadline({ conclusion }: { conclusion: ModelDiagnosisConclusion }): JSX.Element {
  const { t } = useTranslation();
  const layer = t(`admin.modelDiagnosis.layer.${conclusion.layer}`);
  const bold = { b: <strong className="font-semibold" /> };
  if (conclusion.layer === "gateway" && conclusion.verdict === "pass") return <>{t("admin.modelDiagnosis.headline.allPass")}</>;
  if (conclusion.owner === "gateway") return <Trans i18nKey="admin.modelDiagnosis.headline.gateway" values={{ layer }} components={bold} />;
  if (conclusion.owner === "unknown") return <>{t("admin.modelDiagnosis.headline.unknown", { layer })}</>;
  return <Trans i18nKey="admin.modelDiagnosis.headline.stuck" values={{ layer }} components={bold} />;
}

/** 诊断结果（纯渲染）：结论卡 → 五层 × 用法明细表 → 档位点名 → 网关交接参数。 */
export function ModelDiagnosisResultView({ diagnosis }: { diagnosis: ModelAvailabilityDiagnosis }): JSX.Element {
  const { t, i18n } = useTranslation();
  const { sentence, evidence, join } = useFindingText();
  const d = diagnosis;
  const m = d.member;

  const meta = (
    <div className="flex flex-wrap gap-x-3.5 gap-y-1 text-xs text-dosia-text-secondary" data-testid="diagnosis-meta">
      <span>{t("admin.modelDiagnosis.meta.model")} <Code>{d.modelId}</Code></span>
      <span>
        {d.supplySource === "member_bundle"
          ? t("admin.modelDiagnosis.meta.sourceMember", { employee: d.employee ?? "" })
          : t("admin.modelDiagnosis.meta.sourcePublic")}
      </span>
      {m && (
        <span>
          {m.lastFetchedAt
            ? t("admin.modelDiagnosis.meta.lastSync", { time: new Date(m.lastFetchedAt).toLocaleString(i18n.language, { hour12: false }) })
            : t("admin.modelDiagnosis.meta.lastSyncNever")}
          {m.online !== undefined && ` · ${t(m.online ? "admin.modelDiagnosis.meta.online" : "admin.modelDiagnosis.meta.offline")}`}
        </span>
      )}
      {d.orgCode && <span>{t("admin.modelDiagnosis.meta.org", { org: d.orgCode })}</span>}
      {d.roleCode && <span>{t("admin.modelDiagnosis.meta.role", { role: d.roleCode })}</span>}
    </div>
  );

  // 网关清单不是 live：下发链此刻不对齐，诊断不替它猜——整页只说判不了。
  if (!d.catalogLive) {
    return (
      <div className="space-y-2.5">
        {meta}
        <div data-testid="diagnosis-catalog-not-live" role="status" className="rounded-lg border border-dosia-warning/40 bg-dosia-warning/10 px-3.5 py-3 text-sm text-dosia-warning">
          {t("admin.modelDiagnosis.code.catalog_not_live")}
        </div>
      </div>
    );
  }

  const uses = MODEL_DIAGNOSIS_USES.filter((use) => d.conclusions.some((c) => c.use === use));
  const notApplicable = MODEL_DIAGNOSIS_USES.filter((use) => !uses.includes(use));
  const labelOf = (use: ModelDiagnosisUse): string => t(`admin.modelDiagnosis.use.${use}`);

  return (
    <div className="space-y-3.5">
      {meta}

      <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]">
        {uses.map((use) => {
          const c = d.conclusions.find((x) => x.use === use)!;
          // 句子取与结论同层同码的那条 finding（带证据参数）；结论标题与归属只读 conclusion 本身。
          const f = d.findings.find((x) => x.use === use && x.layer === c.layer && x.code === c.code) ?? c;
          return (
            <div key={use} data-testid={`diagnosis-conclusion-${use}`} className="rounded-lg border border-dosia-border bg-dosia-bg-light px-3.5 py-3">
              <h3 className="mb-1.5 text-sm font-medium text-dosia-text">{labelOf(use)}</h3>
              <div data-testid={`diagnosis-headline-${use}`} className="text-sm text-dosia-text">
                <ConclusionHeadline conclusion={c} /> <VerdictBadge verdict={c.verdict} />
              </div>
              <div className="mt-1.5 text-sm text-dosia-text">{sentence(f)}</div>
              <div data-testid={`diagnosis-owner-${use}`} className="mt-1.5 text-xs text-dosia-text-secondary">
                {t(`admin.modelDiagnosis.owner.${c.owner}`)}
              </div>
            </div>
          );
        })}
        {notApplicable.length > 0 && (
          <div data-testid="diagnosis-not-applicable" className="rounded-lg border border-dosia-border bg-dosia-bg-light px-3.5 py-3">
            <h3 className="mb-1.5 text-sm font-medium text-dosia-text">{t("admin.modelDiagnosis.notApplicable.title")}</h3>
            <div className="text-sm text-dosia-text">
              {t("admin.modelDiagnosis.notApplicable.body", { uses: join(notApplicable.map(labelOf)) })}
            </div>
          </div>
        )}
      </div>

      {uses.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-dosia-border">
          <table className="w-full border-collapse text-sm" data-testid="diagnosis-layer-table">
            <thead>
              <tr className="bg-dosia-bg-lighter text-left text-xs text-dosia-text-secondary">
                <th className="px-2.5 py-2 font-medium">{t("admin.modelDiagnosis.table.layer")}</th>
                {uses.map((use) => <th key={use} className="px-2.5 py-2 font-medium">{labelOf(use)}</th>)}
              </tr>
            </thead>
            <tbody>
              {MODEL_DIAGNOSIS_LAYERS.map((layer) => (
                <tr key={layer} className="border-t border-dosia-border align-top">
                  <td className="w-[170px] px-2.5 py-2 font-medium text-dosia-text">{t(`admin.modelDiagnosis.layer.${layer}`)}</td>
                  {uses.map((use) => (
                    <td key={use} data-testid={`diagnosis-cell-${layer}-${use}`} className="space-y-2 px-2.5 py-2">
                      {d.findings.filter((x) => x.use === use && x.layer === layer).map((f, index) => (
                        <div key={`${f.code}-${f.scope ?? index}`}>
                          {f.scope && (
                            <span className="mr-1 text-[11px] text-dosia-text-secondary">
                              {t(f.scope === "role" ? "admin.modelDiagnosis.scope.role" : "admin.modelDiagnosis.scope.org")}
                            </span>
                          )}
                          <VerdictBadge verdict={f.verdict} />
                          <div className="mt-1 text-dosia-text">{sentence(f)}</div>
                          {evidence(f) && <div className="mt-0.5 text-[11px] text-dosia-text-secondary">{evidence(f)}</div>}
                        </div>
                      ))}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {d.tierReferrers === null ? (
        <div data-testid="diagnosis-tier-referrers" className="rounded-lg border border-dosia-border bg-dosia-bg-light px-3.5 py-3 text-sm">
          <h4 className="mb-1.5 font-medium text-dosia-text">{t("admin.modelDiagnosis.tier.title")}</h4>
          <div className="text-xs text-dosia-text-secondary">{t("admin.modelDiagnosis.tier.needEmployee")}</div>
        </div>
      ) : d.tierReferrers.length > 0 && (
        <div data-testid="diagnosis-tier-referrers" className="rounded-lg border border-dosia-border bg-dosia-bg-light px-3.5 py-3 text-sm">
          <h4 className="mb-1.5 font-medium text-dosia-text">{t("admin.modelDiagnosis.tier.title")}</h4>
          {d.tierReferrers.map((r) => (
            <div key={`${r.modelId}-${r.group}`} className="text-dosia-text">
              <Trans
                i18nKey="admin.modelDiagnosis.tier.item"
                values={{ model: r.modelId, group: r.group, tiers: join(r.tiers) }}
                components={{ c: <Code /> }}
              />
            </div>
          ))}
        </div>
      )}

      <div data-testid="diagnosis-handoff" className="rounded-lg border border-dosia-border bg-dosia-bg-light px-3.5 py-3 text-sm">
        <h4 className="mb-1.5 font-medium text-dosia-text">{t("admin.modelDiagnosis.handoff.title")}</h4>
        <div className="text-dosia-text">
          {t("admin.modelDiagnosis.handoff.model")} <Code>{d.gatewayHandoff.modelId}</Code>
          {" · "}
          {t("admin.modelDiagnosis.handoff.groups")}{" "}
          {d.gatewayHandoff.groups.length
            ? d.gatewayHandoff.groups.map((g, i) => <span key={g}>{i > 0 && " "}<Code>{g}</Code></span>)
            : "—"}
          {d.gatewayHandoff.memberName && (
            <>
              {" · "}
              {t("admin.modelDiagnosis.handoff.member")} <Code>{d.gatewayHandoff.memberName}</Code>
            </>
          )}
        </div>
        <div className="mt-1.5 text-xs text-dosia-text-secondary">{t("admin.modelDiagnosis.handoff.note")}</div>
      </div>
    </div>
  );
}

type DiagnosisState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "result"; diagnosis: ModelAvailabilityDiagnosis }
  | { kind: "unparseable" }
  | { kind: "error"; code: string; message: string };

const KNOWN_ERRORS: Readonly<Partial<Record<string, string>>> = {
  "model-required": "admin.modelDiagnosis.error.modelRequired",
  "org-required-for-employee": "admin.modelDiagnosis.error.orgRequiredForEmployee",
  "org-not-found": "admin.modelDiagnosis.error.orgNotFound",
  "employee-not-found": "admin.modelDiagnosis.error.employeeNotFound",
};

/** 诊断页签：表单（模型 / 可选组织 / 可选员工）+ 结果。`modelIds` 是网关清单的 base id，只做输入候选。 */
export function PlatformModelDiagnosis({ modelIds }: { modelIds: readonly string[] }): JSX.Element {
  const { t } = useTranslation();
  const token = useAtomValue(authAtom)?.token;
  const orgsQ = useApiQuery<Organization[]>("/api/organizations", []);
  const orgs = Array.isArray(orgsQ.data) ? orgsQ.data : [];
  const [model, setModel] = useState("");
  const [org, setOrg] = useState("");
  const [employee, setEmployee] = useState("");
  const [state, setState] = useState<DiagnosisState>({ kind: "idle" });
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);
  const knownErrorKey = state.kind === "error" ? KNOWN_ERRORS[state.code] : undefined;

  async function run(event: FormEvent): Promise<void> {
    event.preventDefault();
    const modelId = model.trim();
    if (!token || !modelId) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setState({ kind: "loading" });
    try {
      const path = platformModelDiagnosisPath({
        model: modelId,
        ...(org ? { org } : {}),
        ...(org && employee.trim() ? { employee: employee.trim() } : {}),
      });
      const raw = await apiCall<unknown>("GET", path, { token, signal: controller.signal });
      if (controller.signal.aborted) return;
      const diagnosis = parseModelAvailabilityDiagnosis(raw);
      setState(diagnosis ? { kind: "result", diagnosis } : { kind: "unparseable" });
    } catch (err) {
      if (controller.signal.aborted) return;
      setState({
        kind: "error",
        code: err instanceof ApiError ? err.code : "client-error",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }

  return (
    <div data-testid="model-diagnosis-panel" className="space-y-3.5">
      <form onSubmit={(event) => void run(event)} className="rounded-lg border border-dosia-border bg-dosia-bg-light px-3.5 py-3">
        <div className="grid items-end gap-2.5 md:grid-cols-[1.4fr_1fr_1fr_auto]">
          <FormField label={t("admin.modelDiagnosis.form.model")}>
            <TextInput value={model} onChange={(e) => setModel(e.target.value)} list="model-diagnosis-models" />
            <datalist id="model-diagnosis-models">
              {modelIds.map((id) => <option key={id} value={id} />)}
            </datalist>
          </FormField>
          <FormField label={t("admin.modelDiagnosis.form.org")}>
            <Select value={org} onChange={(e) => setOrg(e.target.value)}>
              <option value="">{t("admin.modelDiagnosis.form.orgNone")}</option>
              {orgs.map((o) => <option key={o.code} value={o.code}>{`${o.name} · ${o.code}`}</option>)}
            </Select>
          </FormField>
          <FormField label={t("admin.modelDiagnosis.form.employee")}>
            <TextInput
              value={employee}
              onChange={(e) => setEmployee(e.target.value)}
              disabled={!org}
              placeholder={org ? "" : t("admin.modelDiagnosis.form.employeeNeedsOrg")}
            />
          </FormField>
          <PrimaryButton type="submit" disabled={!model.trim() || state.kind === "loading"}>
            {state.kind === "loading" ? t("admin.modelDiagnosis.form.running") : t("admin.modelDiagnosis.form.submit")}
          </PrimaryButton>
        </div>
        <div className="mt-2 text-xs text-dosia-text-secondary">{t("admin.modelDiagnosis.form.hint")}</div>
      </form>

      {state.kind === "unparseable" && (
        <ErrorBanner><span data-testid="diagnosis-unparseable">{t("admin.modelDiagnosis.error.unparseable")}</span></ErrorBanner>
      )}
      {state.kind === "error" && (
        <ErrorBanner>
          {knownErrorKey ? t(knownErrorKey) : t("admin.modelDiagnosis.error.generic", { message: state.message })}
        </ErrorBanner>
      )}
      {state.kind === "result" && <ModelDiagnosisResultView diagnosis={state.diagnosis} />}
    </div>
  );
}
